/**
 * @engine/vfx - Operator Nodes
 *
 * Math, random, and utility nodes available in any context.
 */

import type { VFXNodeDefinition } from '../../types';
import { dataPort } from '../../types';

/**
 * Operator node definitions.
 */

export const MATH_NODES: readonly VFXNodeDefinition[] = [

  {
    id: 'vfx.operator.add',
    name: 'Add',
    category: 'operator',
    contexts: [],
    description: 'Add two values',
    color: '#607D8B',
    inputs: [
      dataPort('a', 'A', 'float', { defaultValue: 0 }),
      dataPort('b', 'B', 'float', { defaultValue: 0 }),
    ],
    outputs: [
      dataPort('result', 'Result', 'float'),
    ],
    shader: {
      code: `result = a + b;`,
    },
  },

  {
    id: 'vfx.operator.subtract',
    name: 'Subtract',
    category: 'operator',
    contexts: [],
    description: 'Subtract two values',
    color: '#607D8B',
    inputs: [
      dataPort('a', 'A', 'float', { defaultValue: 0 }),
      dataPort('b', 'B', 'float', { defaultValue: 0 }),
    ],
    outputs: [
      dataPort('result', 'Result', 'float'),
    ],
    shader: {
      code: `result = a - b;`,
    },
  },

  {
    id: 'vfx.operator.multiply',
    name: 'Multiply',
    category: 'operator',
    contexts: [],
    description: 'Multiply two values',
    color: '#607D8B',
    inputs: [
      dataPort('a', 'A', 'float', { defaultValue: 1 }),
      dataPort('b', 'B', 'float', { defaultValue: 1 }),
    ],
    outputs: [
      dataPort('result', 'Result', 'float'),
    ],
    shader: {
      code: `result = a * b;`,
    },
  },

  {
    id: 'vfx.operator.divide',
    name: 'Divide',
    category: 'operator',
    contexts: [],
    description: 'Divide two values',
    color: '#607D8B',
    inputs: [
      dataPort('a', 'A', 'float', { defaultValue: 1 }),
      dataPort('b', 'B', 'float', { defaultValue: 1 }),
    ],
    outputs: [
      dataPort('result', 'Result', 'float'),
    ],
    shader: {
      code: `result = a / max(b, 0.0001);`,
    },
  },


  {
    id: 'vfx.operator.vec3_compose',
    name: 'Compose Vec3',
    category: 'operator',
    contexts: [],
    description: 'Create vec3 from components',
    color: '#607D8B',
    inputs: [
      dataPort('x', 'X', 'float', { defaultValue: 0 }),
      dataPort('y', 'Y', 'float', { defaultValue: 0 }),
      dataPort('z', 'Z', 'float', { defaultValue: 0 }),
    ],
    outputs: [
      dataPort('result', 'Result', 'vec3'),
    ],
    shader: {
      code: `result = float3(x, y, z);`,
    },
  },

  {
    id: 'vfx.operator.vec3_decompose',
    name: 'Decompose Vec3',
    category: 'operator',
    contexts: [],
    description: 'Extract components from vec3',
    color: '#607D8B',
    inputs: [
      dataPort('v', 'Vector', 'vec3', { defaultValue: { x: 0, y: 0, z: 0 } }),
    ],
    outputs: [
      dataPort('x', 'X', 'float'),
      dataPort('y', 'Y', 'float'),
      dataPort('z', 'Z', 'float'),
    ],
    shader: {
      code: `x = v.x; y = v.y; z = v.z;`,
    },
  },

  {
    id: 'vfx.operator.normalize',
    name: 'Normalize',
    category: 'operator',
    contexts: [],
    description: 'Normalize vector to unit length',
    color: '#607D8B',
    inputs: [
      dataPort('v', 'Vector', 'vec3'),
    ],
    outputs: [
      dataPort('result', 'Result', 'vec3'),
    ],
    shader: {
      code: `result = normalize(v);`,
    },
  },

  {
    id: 'vfx.operator.length',
    name: 'Length',
    category: 'operator',
    contexts: [],
    description: 'Get vector length',
    color: '#607D8B',
    inputs: [
      dataPort('v', 'Vector', 'vec3'),
    ],
    outputs: [
      dataPort('result', 'Result', 'float'),
    ],
    shader: {
      code: `result = length(v);`,
    },
  },

  {
    id: 'vfx.operator.dot',
    name: 'Dot Product',
    category: 'operator',
    contexts: [],
    description: 'Dot product of two vectors',
    color: '#607D8B',
    inputs: [
      dataPort('a', 'A', 'vec3'),
      dataPort('b', 'B', 'vec3'),
    ],
    outputs: [
      dataPort('result', 'Result', 'float'),
    ],
    shader: {
      code: `result = dot(a, b);`,
    },
  },

  {
    id: 'vfx.operator.cross',
    name: 'Cross Product',
    category: 'operator',
    contexts: [],
    description: 'Cross product of two vectors',
    color: '#607D8B',
    inputs: [
      dataPort('a', 'A', 'vec3'),
      dataPort('b', 'B', 'vec3'),
    ],
    outputs: [
      dataPort('result', 'Result', 'vec3'),
    ],
    shader: {
      code: `result = cross(a, b);`,
    },
  },


  {
    id: 'vfx.operator.lerp',
    name: 'Lerp',
    category: 'operator',
    contexts: [],
    description: 'Linear interpolation',
    color: '#607D8B',
    inputs: [
      dataPort('a', 'A', 'float', { defaultValue: 0 }),
      dataPort('b', 'B', 'float', { defaultValue: 1 }),
      dataPort('t', 'T', 'float', { defaultValue: 0.5, range: { min: 0, max: 1 } }),
    ],
    outputs: [
      dataPort('result', 'Result', 'float'),
    ],
    shader: {
      code: `result = lerp(a, b, t);`,
    },
  },

  {
    id: 'vfx.operator.smoothstep',
    name: 'Smoothstep',
    category: 'operator',
    contexts: [],
    description: 'Smooth hermite interpolation',
    color: '#607D8B',
    inputs: [
      dataPort('edge0', 'Edge 0', 'float', { defaultValue: 0 }),
      dataPort('edge1', 'Edge 1', 'float', { defaultValue: 1 }),
      dataPort('x', 'X', 'float', { defaultValue: 0.5 }),
    ],
    outputs: [
      dataPort('result', 'Result', 'float'),
    ],
    shader: {
      code: `result = smoothstep(edge0, edge1, x);`,
    },
  },

  {
    id: 'vfx.operator.clamp',
    name: 'Clamp',
    category: 'operator',
    contexts: [],
    description: 'Clamp value to range',
    color: '#607D8B',
    inputs: [
      dataPort('value', 'Value', 'float', { defaultValue: 0 }),
      dataPort('min', 'Min', 'float', { defaultValue: 0 }),
      dataPort('max', 'Max', 'float', { defaultValue: 1 }),
    ],
    outputs: [
      dataPort('result', 'Result', 'float'),
    ],
    shader: {
      code: `result = clamp(value, min, max);`,
    },
  },

  {
    id: 'vfx.operator.remap',
    name: 'Remap',
    category: 'operator',
    contexts: [],
    description: 'Remap value from one range to another',
    color: '#607D8B',
    inputs: [
      dataPort('value', 'Value', 'float', { defaultValue: 0 }),
      dataPort('inMin', 'In Min', 'float', { defaultValue: 0 }),
      dataPort('inMax', 'In Max', 'float', { defaultValue: 1 }),
      dataPort('outMin', 'Out Min', 'float', { defaultValue: 0 }),
      dataPort('outMax', 'Out Max', 'float', { defaultValue: 1 }),
    ],
    outputs: [
      dataPort('result', 'Result', 'float'),
    ],
    shader: {
      code: `
        float t = (value - inMin) / (inMax - inMin);
        result = lerp(outMin, outMax, t);
      `,
    },
  },


  {
    id: 'vfx.operator.sin',
    name: 'Sin',
    category: 'operator',
    contexts: [],
    color: '#607D8B',
    inputs: [dataPort('x', 'X', 'float')],
    outputs: [dataPort('result', 'Result', 'float')],
    shader: {
      code: `result = sin(x);`,
    },
  },

  {
    id: 'vfx.operator.cos',
    name: 'Cos',
    category: 'operator',
    contexts: [],
    color: '#607D8B',
    inputs: [dataPort('x', 'X', 'float')],
    outputs: [dataPort('result', 'Result', 'float')],
    shader: {
      code: `result = cos(x);`,
    },
  },

  {
    id: 'vfx.operator.atan2',
    name: 'Atan2',
    category: 'operator',
    contexts: [],
    color: '#607D8B',
    inputs: [
      dataPort('y', 'Y', 'float'),
      dataPort('x', 'X', 'float'),
    ],
    outputs: [dataPort('result', 'Result', 'float')],
    shader: {
      code: `result = atan2(y, x);`,
    },
  },


  {
    id: 'vfx.operator.floor',
    name: 'Floor',
    category: 'operator',
    contexts: [],
    description: 'Round down to nearest integer',
    color: '#607D8B',
    inputs: [
      dataPort('x', 'X', 'float', { defaultValue: 0 }),
    ],
    outputs: [
      dataPort('result', 'Result', 'float'),
    ],
    shader: {
      code: `result = floor(x);`,
    },
  },

  {
    id: 'vfx.operator.ceil',
    name: 'Ceil',
    category: 'operator',
    contexts: [],
    description: 'Round up to nearest integer',
    color: '#607D8B',
    inputs: [
      dataPort('x', 'X', 'float', { defaultValue: 0 }),
    ],
    outputs: [
      dataPort('result', 'Result', 'float'),
    ],
    shader: {
      code: `result = ceil(x);`,
    },
  },

  {
    id: 'vfx.operator.round',
    name: 'Round',
    category: 'operator',
    contexts: [],
    description: 'Round to nearest integer',
    color: '#607D8B',
    inputs: [
      dataPort('x', 'X', 'float', { defaultValue: 0 }),
    ],
    outputs: [
      dataPort('result', 'Result', 'float'),
    ],
    shader: {
      code: `result = round(x);`,
    },
  },

  {
    id: 'vfx.operator.fract',
    name: 'Fract',
    category: 'operator',
    contexts: [],
    description: 'Get fractional part of a number',
    color: '#607D8B',
    inputs: [
      dataPort('x', 'X', 'float', { defaultValue: 0 }),
    ],
    outputs: [
      dataPort('result', 'Result', 'float'),
    ],
    shader: {
      code: `result = frac(x);`,
    },
  },

  {
    id: 'vfx.operator.modulo',
    name: 'Modulo',
    category: 'operator',
    contexts: [],
    description: 'Get remainder of division (a % b)',
    color: '#607D8B',
    inputs: [
      dataPort('a', 'A', 'float', { defaultValue: 0 }),
      dataPort('b', 'B', 'float', { defaultValue: 1 }),
    ],
    outputs: [
      dataPort('result', 'Result', 'float'),
    ],
    shader: {
      code: `result = a - b * floor(a / max(b, 0.0001));`,
    },
  },

  {
    id: 'vfx.operator.modulo_int',
    name: 'Modulo (Int)',
    category: 'operator',
    contexts: [],
    description: 'Get remainder of integer division (a % b)',
    color: '#607D8B',
    inputs: [
      dataPort('a', 'A', 'uint', { defaultValue: 0 }),
      dataPort('b', 'B', 'uint', { defaultValue: 1 }),
    ],
    outputs: [
      dataPort('result', 'Result', 'uint'),
    ],
    shader: {
      code: `result = a % max(b, 1u);`,
    },
  },

  {
    id: 'vfx.operator.abs',
    name: 'Abs',
    category: 'operator',
    contexts: [],
    description: 'Absolute value',
    color: '#607D8B',
    inputs: [
      dataPort('x', 'X', 'float', { defaultValue: 0 }),
    ],
    outputs: [
      dataPort('result', 'Result', 'float'),
    ],
    shader: {
      code: `result = abs(x);`,
    },
  },

  {
    id: 'vfx.operator.sign',
    name: 'Sign',
    category: 'operator',
    contexts: [],
    description: 'Sign of value (-1, 0, or 1)',
    color: '#607D8B',
    inputs: [
      dataPort('x', 'X', 'float', { defaultValue: 0 }),
    ],
    outputs: [
      dataPort('result', 'Result', 'float'),
    ],
    shader: {
      code: `result = sign(x);`,
    },
  },

  {
    id: 'vfx.operator.min',
    name: 'Min',
    category: 'operator',
    contexts: [],
    description: 'Minimum of two values',
    color: '#607D8B',
    inputs: [
      dataPort('a', 'A', 'float', { defaultValue: 0 }),
      dataPort('b', 'B', 'float', { defaultValue: 0 }),
    ],
    outputs: [
      dataPort('result', 'Result', 'float'),
    ],
    shader: {
      code: `result = min(a, b);`,
    },
  },

  {
    id: 'vfx.operator.max',
    name: 'Max',
    category: 'operator',
    contexts: [],
    description: 'Maximum of two values',
    color: '#607D8B',
    inputs: [
      dataPort('a', 'A', 'float', { defaultValue: 0 }),
      dataPort('b', 'B', 'float', { defaultValue: 0 }),
    ],
    outputs: [
      dataPort('result', 'Result', 'float'),
    ],
    shader: {
      code: `result = max(a, b);`,
    },
  },

  {
    id: 'vfx.operator.pow',
    name: 'Power',
    category: 'operator',
    contexts: [],
    description: 'Raise to power (a^b)',
    color: '#607D8B',
    inputs: [
      dataPort('a', 'A', 'float', { defaultValue: 2 }),
      dataPort('b', 'B', 'float', { defaultValue: 2 }),
    ],
    outputs: [
      dataPort('result', 'Result', 'float'),
    ],
    shader: {
      code: `result = pow(a, b);`,
    },
  },

  {
    id: 'vfx.operator.sqrt',
    name: 'Sqrt',
    category: 'operator',
    contexts: [],
    description: 'Square root',
    color: '#607D8B',
    inputs: [
      dataPort('x', 'X', 'float', { defaultValue: 1 }),
    ],
    outputs: [
      dataPort('result', 'Result', 'float'),
    ],
    shader: {
      code: `result = sqrt(max(x, 0.0));`,
    },
  },

  {
    id: 'vfx.operator.exp',
    name: 'Exp',
    category: 'operator',
    contexts: [],
    description: 'Exponential (e^x)',
    color: '#607D8B',
    inputs: [
      dataPort('x', 'X', 'float', { defaultValue: 0 }),
    ],
    outputs: [
      dataPort('result', 'Result', 'float'),
    ],
    shader: {
      code: `result = exp(x);`,
    },
  },

  {
    id: 'vfx.operator.log',
    name: 'Log',
    category: 'operator',
    contexts: [],
    description: 'Natural logarithm',
    color: '#607D8B',
    inputs: [
      dataPort('x', 'X', 'float', { defaultValue: 1 }),
    ],
    outputs: [
      dataPort('result', 'Result', 'float'),
    ],
    shader: {
      code: `result = log(max(x, 0.0001));`,
    },
  },


  {
    id: 'vfx.operator.distance',
    name: 'Distance',
    category: 'operator',
    contexts: [],
    description: 'Distance between two points',
    color: '#607D8B',
    inputs: [
      dataPort('a', 'A', 'vec3', { defaultValue: { x: 0, y: 0, z: 0 } }),
      dataPort('b', 'B', 'vec3', { defaultValue: { x: 0, y: 0, z: 0 } }),
    ],
    outputs: [
      dataPort('result', 'Result', 'float'),
    ],
    shader: {
      code: `result = distance(a, b);`,
    },
  },

  {
    id: 'vfx.operator.distance_2d',
    name: 'Distance 2D',
    category: 'operator',
    contexts: [],
    description: 'Distance between two 2D points',
    color: '#607D8B',
    inputs: [
      dataPort('a', 'A', 'vec2', { defaultValue: { x: 0, y: 0 } }),
      dataPort('b', 'B', 'vec2', { defaultValue: { x: 0, y: 0 } }),
    ],
    outputs: [
      dataPort('result', 'Result', 'float'),
    ],
    shader: {
      code: `result = distance(a, b);`,
    },
  },

  {
    id: 'vfx.operator.vec3_add',
    name: 'Add Vec3',
    category: 'operator',
    contexts: [],
    description: 'Add two vec3',
    color: '#607D8B',
    inputs: [
      dataPort('a', 'A', 'vec3', { defaultValue: { x: 0, y: 0, z: 0 } }),
      dataPort('b', 'B', 'vec3', { defaultValue: { x: 0, y: 0, z: 0 } }),
    ],
    outputs: [
      dataPort('result', 'Result', 'vec3'),
    ],
    shader: {
      code: `result = a + b;`,
    },
  },

  {
    id: 'vfx.operator.vec3_subtract',
    name: 'Subtract Vec3',
    category: 'operator',
    contexts: [],
    description: 'Subtract two vec3',
    color: '#607D8B',
    inputs: [
      dataPort('a', 'A', 'vec3', { defaultValue: { x: 0, y: 0, z: 0 } }),
      dataPort('b', 'B', 'vec3', { defaultValue: { x: 0, y: 0, z: 0 } }),
    ],
    outputs: [
      dataPort('result', 'Result', 'vec3'),
    ],
    shader: {
      code: `result = a - b;`,
    },
  },

  {
    id: 'vfx.operator.vec3_multiply',
    name: 'Multiply Vec3',
    category: 'operator',
    contexts: [],
    description: 'Multiply vec3 by scalar',
    color: '#607D8B',
    inputs: [
      dataPort('v', 'Vector', 'vec3', { defaultValue: { x: 1, y: 1, z: 1 } }),
      dataPort('s', 'Scalar', 'float', { defaultValue: 1 }),
    ],
    outputs: [
      dataPort('result', 'Result', 'vec3'),
    ],
    shader: {
      code: `result = v * s;`,
    },
  },

  {
    id: 'vfx.operator.vec3_lerp',
    name: 'Lerp Vec3',
    category: 'operator',
    contexts: [],
    description: 'Linear interpolation between vec3',
    color: '#607D8B',
    inputs: [
      dataPort('a', 'A', 'vec3', { defaultValue: { x: 0, y: 0, z: 0 } }),
      dataPort('b', 'B', 'vec3', { defaultValue: { x: 1, y: 1, z: 1 } }),
      dataPort('t', 'T', 'float', { defaultValue: 0.5, range: { min: 0, max: 1 } }),
    ],
    outputs: [
      dataPort('result', 'Result', 'vec3'),
    ],
    shader: {
      code: `result = lerp(a, b, t);`,
    },
  },
];
